/**
 * Validation and slug-suggestion helpers for extending the RBAC permission
 * catalog (module -> allowed actions) from the UI.
 *
 * The catalog is what the New-permission form picks from. Until now it could
 * only grow by editing \Aman\Seeders\PermissionCatalogDefaults and deploying,
 * so "the action I need isn't listed" was a dead end. These helpers back the
 * inline "New module" / "New action" forms that remove that dead end.
 *
 * Every rule here mirrors RBACService::createPermissionModule /
 * ::createModuleAction. The server stays the authority — this only moves the
 * rejection to before the request, where the admin can still see the field
 * that caused it.
 */

/** RBACService::createPermissionModule — module_key. */
export const MODULE_KEY_PATTERN = /^[a-z][a-z0-9_]*$/;
/** RBACService::createModuleAction — resource. No dots: resource is one segment. */
export const RESOURCE_PATTERN = /^[a-z][a-z0-9_]*$/;
/** RBACService::createModuleAction — action. Dots allowed: `branches.manage`. */
export const ACTION_PATTERN = /^[a-z][a-z0-9_.]*$/;

/**
 * Resources the catalog deliberately does not describe.
 *
 * `platform.*` is CLI-managed and rejected by the server outright. Listing it
 * here turns a 400 into a sentence that says why.
 */
export const UNCATALOGUABLE_RESOURCES = ['platform'];

/**
 * A human label turned into a candidate slug segment.
 *
 * Only a suggestion — the admin can always overwrite it, and the pattern
 * checks below are what actually decide. `allowDots` is for actions, whose
 * slugs may be namespaced (`branches.manage`).
 */
export const slugify = (value, { allowDots = false } = {}) => {
  const cleaned = String(value ?? '')
    .toLowerCase()
    .trim()
    .replace(/['’]/g, '')
    .replace(allowDots ? /[^a-z0-9.]+/g : /[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  // A leading digit fails both patterns; the label is still the useful part.
  return cleaned.replace(/^[^a-z]+/, '');
};

/**
 * The resource a new action in this module should default to.
 *
 * The module key is a poor default on its own — Staff & Users is `staff` but
 * its actions are `users.*` and `staff.*`, and Pre-Authorization is `preauth`
 * carrying `pa.*`. The resource already used most often in the module is a far
 * better guess, so the common case needs no typing and the unusual case
 * (facility_ratings under Facilities) is still reachable by overwriting it.
 */
export const suggestResourceForModule = (moduleKey, actions = []) => {
  const counts = new Map();
  (actions || []).forEach((a) => {
    const resource = a?.resource;
    if (!resource) return;
    counts.set(resource, (counts.get(resource) || 0) + 1);
  });
  if (counts.size === 0) return slugify(moduleKey);
  // Ties break alphabetically so the suggestion is stable across reloads
  // rather than following whatever order the query happened to return.
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0][0];
};

/**
 * @returns {{ok: boolean, errors: Object<string,string>}} field -> message
 */
export const validateModuleDraft = (draft = {}, existingModules = []) => {
  const errors = {};
  const label = String(draft.label ?? '').trim();
  const moduleKey = String(draft.module_key ?? '').trim().toLowerCase();

  if (!label) errors.label = 'Label is required';
  if (!moduleKey) {
    errors.module_key = 'Key is required';
  } else if (!MODULE_KEY_PATTERN.test(moduleKey)) {
    errors.module_key = 'Use lowercase letters, digits and underscores, starting with a letter';
  } else if (existingModules.some((m) => m?.module_key === moduleKey)) {
    errors.module_key = 'A module with that key already exists';
  }

  return { ok: Object.keys(errors).length === 0, errors };
};

/**
 * @param existingActions catalog actions to check the slug against. Pass the
 *   whole catalog, not just the chosen module's: the server rejects a
 *   resource/action pair that is catalogued anywhere, because the pair — not
 *   the module — is what a permission is made of.
 */
export const validateActionDraft = (draft = {}, existingActions = []) => {
  const errors = {};
  const label = String(draft.label ?? '').trim();
  const resource = String(draft.resource ?? '').trim().toLowerCase();
  const action = String(draft.action ?? '').trim().toLowerCase();

  if (!label) errors.label = 'Label is required';

  if (!resource) {
    errors.resource = 'Resource is required';
  } else if (UNCATALOGUABLE_RESOURCES.includes(resource)) {
    errors.resource = `${resource}.* permissions are managed outside the catalog and cannot be added here`;
  } else if (!RESOURCE_PATTERN.test(resource)) {
    errors.resource = 'Use lowercase letters, digits and underscores, starting with a letter';
  }

  if (!action) {
    errors.action = 'Action is required';
  } else if (!ACTION_PATTERN.test(action)) {
    // This is also what rejects `*`: a wildcard is not an action, and
    // admin.* must never become a tickable row next to ordinary capabilities.
    errors.action = 'Use lowercase letters, digits, underscores and dots — wildcards are not actions';
  }

  if (!errors.resource && !errors.action) {
    const slug = `${resource}.${action}`;
    const clash = existingActions.find((a) => `${a?.resource}.${a?.action}` === slug);
    if (clash) {
      errors.action = `${slug} is already catalogued under "${clash.module_label || clash.module_key}"`;
    }
  }

  return { ok: Object.keys(errors).length === 0, errors };
};

/** The permission slug a draft action would produce, for the form preview. */
export const draftActionSlug = (draft = {}) => {
  const resource = String(draft.resource ?? '').trim().toLowerCase();
  const action = String(draft.action ?? '').trim().toLowerCase();
  if (!resource || !action) return '';
  return `${resource}.${action}`;
};
